import { useEffect, useState, useCallback } from "react";

// Espace expert-comptable — session distincte de l'app principale (JWT
// EXPERT_JWT_SECRET, cf. server/routes/expertAccess.js). Gère son propre
// état de session dans une clé localStorage dédiée (jamais financiar_token /
// TOKEN_KEY de src/services/api.js) pour ne jamais contaminer une session
// cliente réelle ouverte dans le même navigateur.
const EXPERT_TOKEN_KEY = "expert_access_token";

const S = {
  page: { minHeight: "100vh", background: "#0d1117", color: "#f1f5f9", fontFamily: "inherit" },
  center: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" },
  card: { maxWidth: 420 },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 10 },
  msg: { fontSize: 14, color: "#94a3b8", lineHeight: 1.6 },
  header: { padding: "16px 24px", borderBottom: "1px solid #1f242c", display: "flex", justifyContent: "space-between", alignItems: "center" },
  badge: { fontSize: 11, padding: "3px 10px", borderRadius: 999, background: "rgba(46,204,143,0.1)", color: "#2ECC8F", border: "1px solid rgba(46,204,143,0.25)" },
  main: { padding: 24, maxWidth: 960, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 },
  kpiCard: { background: "#161b22", border: "1px solid #1f242c", borderRadius: 10, padding: "14px 16px" },
  kpiLabel: { fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" },
  kpiValue: { fontSize: 20, fontWeight: 600, marginTop: 4 },
  section: { background: "#161b22", border: "1px solid #1f242c", borderRadius: 10, padding: 16 },
  sectionTitle: { fontSize: 13, fontWeight: 600, marginBottom: 12 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", color: "#6b7280", fontWeight: 500, borderBottom: "1px solid #1f242c" },
  td: { padding: "6px 8px", borderBottom: "1px solid #1a1f26" },
  noteInput: { width: "100%", minHeight: 60, background: "#0d1117", border: "1px solid #1f242c", borderRadius: 6, color: "#f1f5f9", padding: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical" },
  btn: { marginTop: 8, padding: "8px 16px", borderRadius: 6, background: "#2ECC8F", color: "#0d1f17", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer" },
  readonlyNote: { fontSize: 12, color: "#6b7280", fontStyle: "italic" },
};

function fmtEUR(n) {
  return (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

// Statut de dossier — organisationnel côté cabinet uniquement (external_accesses.
// dossier_status), sans aucun lien avec companies.status. Cf. server/routes/
// expertAccess.js PATCH /dossier-status.
const DOSSIER_STATUS_OPTIONS = [
  { value: "a_traiter", label: "À traiter" },
  { value: "en_cours",  label: "En cours" },
  { value: "cloture",   label: "Clôturé" },
];

export default function ExpertAccess() {
  const [status, setStatus]   = useState("loading"); // loading | ready | error
  const [error, setError]     = useState("");
  const [session, setSession] = useState(null); // { expert_name, company_name, permission_level, rights, expert_token }
  const [dashboard, setDashboard] = useState(null);
  const [invoices, setInvoices]   = useState([]);
  const [notes, setNotes]         = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [dossierStatus, setDossierStatus] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const [switching, setSwitching] = useState(false);

  const authedFetch = useCallback((path, opts = {}) => {
    const expertToken = localStorage.getItem(EXPERT_TOKEN_KEY);
    return fetch(`/api/expert${path}`, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(expertToken ? { Authorization: `Bearer ${expertToken}` } : {}),
        ...(opts.headers || {}),
      },
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || "Erreur de communication avec le serveur.");
      return data;
    });
  }, []);

  const loadAll = useCallback(() => {
    return Promise.all([
      authedFetch("/me"),
      authedFetch("/dashboard"),
      authedFetch("/invoices?limit=25"),
      authedFetch("/notes"),
      authedFetch("/portfolio"),
    ]).then(([me, dash, inv, notesData, portfolioData]) => {
      setSession(me);
      setDashboard(dash);
      setInvoices(inv.invoices || []);
      setNotes(notesData || []);
      setDossierStatus(me.dossier_status || "a_traiter");
      setPortfolio(portfolioData || []);
      setStatus("ready");
    });
  }, [authedFetch]);

  // Bascule vers un autre dossier du portefeuille (même email expert) —
  // POST /portfolio/:access_id/enter émet une nouvelle session standard,
  // revérifiée côté serveur ; on la stocke puis on recharge tout l'écran.
  const switchCompany = async (accessId) => {
    setSwitching(true);
    try {
      const res = await authedFetch(`/portfolio/${accessId}/enter`, { method: "POST" });
      localStorage.setItem(EXPERT_TOKEN_KEY, res.token);
      await loadAll();
    } catch (err) {
      window.alert(err.message || "Impossible de basculer vers ce dossier.");
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get("token");
    const existingSession = localStorage.getItem(EXPERT_TOKEN_KEY);

    const bootstrap = async () => {
      try {
        if (inviteToken) {
          const res = await fetch(`/api/expert/${inviteToken}`).then(async (r) => {
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.error || "Lien invalide.");
            return data;
          });
          localStorage.setItem(EXPERT_TOKEN_KEY, res.token);
          window.history.replaceState({}, "", window.location.pathname);
        } else if (!existingSession) {
          setStatus("error");
          setError("Lien d'accès manquant. Redemandez une invitation à la société concernée.");
          return;
        }
        await loadAll();
      } catch (err) {
        localStorage.removeItem(EXPERT_TOKEN_KEY);
        setStatus("error");
        setError(err.message || "Impossible d'établir la session.");
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeDossierStatus = async (value) => {
    if (value === dossierStatus) return;
    const previous = dossierStatus;
    setDossierStatus(value); // optimiste
    setSavingStatus(true);
    try {
      await authedFetch("/dossier-status", { method: "PATCH", body: JSON.stringify({ dossier_status: value }) });
    } catch (err) {
      setDossierStatus(previous);
      window.alert(err.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const submitNote = async () => {
    if (!noteDraft.trim()) return;
    setSavingNote(true);
    try {
      await authedFetch("/notes", { method: "POST", body: JSON.stringify({ content: noteDraft.trim() }) });
      setNoteDraft("");
      const notesData = await authedFetch("/notes");
      setNotes(notesData || []);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setSavingNote(false);
    }
  };

  if (status === "loading") {
    return (
      <div style={S.page}>
        <div style={S.center}>
          <div style={S.card}>
            <div style={S.title}>Connexion à votre espace…</div>
            <div style={S.msg}>Un instant, nous vérifions votre accès.</div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={S.page}>
        <div style={S.center}>
          <div style={S.card}>
            <div style={S.title}>Accès indisponible</div>
            <div style={S.msg}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  const canWrite = session?.rights?.includes("write");

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{session.company_name}</div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>Espace expert-comptable — {session.expert_name}</div>
        </div>
        <span style={S.badge}>{session.permission_level}</span>
      </div>

      <div style={S.main}>
        {portfolio.length > 1 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Mes dossiers ({portfolio.length})</div>
            <select
              style={{ ...S.noteInput, minHeight: "auto", width: "auto", padding: "6px 10px" }}
              value={portfolio.find((p) => p.is_current)?.access_id || ""}
              disabled={switching}
              onChange={(e) => switchCompany(Number(e.target.value))}
            >
              {portfolio.map((p) => (
                <option key={p.access_id} value={p.access_id}>
                  {p.company_name} — {p.permission_level}
                  {p.dossier_status ? ` — ${DOSSIER_STATUS_OPTIONS.find((o) => o.value === p.dossier_status)?.label || p.dossier_status}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={S.section}>
          <div style={S.sectionTitle}>Statut de dossier</div>
          {canWrite ? (
            <select
              style={{ ...S.noteInput, minHeight: "auto", width: "auto", padding: "6px 10px" }}
              value={dossierStatus || "a_traiter"}
              disabled={savingStatus}
              onChange={(e) => changeDossierStatus(e.target.value)}
            >
              {DOSSIER_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <div style={S.msg}>
              {DOSSIER_STATUS_OPTIONS.find((o) => o.value === dossierStatus)?.label || dossierStatus}
              <span style={S.readonlyNote}> — accès en lecture seule, modification impossible.</span>
            </div>
          )}
        </div>

        <div style={S.kpiGrid}>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>CA du mois</div>
            <div style={S.kpiValue}>{fmtEUR(dashboard.ca_month)}</div>
          </div>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>Impayés</div>
            <div style={S.kpiValue}>{fmtEUR(dashboard.unpaid_amount)}</div>
          </div>
          <div style={S.kpiCard}>
            <div style={S.kpiLabel}>Solde bancaire</div>
            <div style={S.kpiValue}>{fmtEUR(dashboard.bank_balance)}</div>
          </div>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Factures récentes</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>N°</th>
                <th style={S.th}>Date</th>
                <th style={S.th}>Client</th>
                <th style={S.th}>Montant TTC</th>
                <th style={S.th}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td style={S.td}>{inv.number}</td>
                  <td style={S.td}>{inv.date}</td>
                  <td style={S.td}>{inv.client_name}</td>
                  <td style={S.td}>{fmtEUR(inv.ttc_amount)}</td>
                  <td style={S.td}>{inv.status}</td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td style={S.td} colSpan={5}>Aucune facture.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={S.section}>
          <div style={S.sectionTitle}>Notes</div>
          {notes.map((n) => (
            <div key={n.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #1a1f26" }}>
              <div style={{ fontSize: 13 }}>{n.content}</div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>{n.author_name} · {n.created_at}</div>
            </div>
          ))}
          {notes.length === 0 && <div style={S.msg}>Aucune note pour le moment.</div>}

          {canWrite ? (
            <div style={{ marginTop: 12 }}>
              <textarea
                style={S.noteInput}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Ajouter une note…"
              />
              <button style={S.btn} onClick={submitNote} disabled={savingNote || !noteDraft.trim()}>
                {savingNote ? "Enregistrement…" : "Ajouter la note"}
              </button>
            </div>
          ) : (
            <div style={{ ...S.readonlyNote, marginTop: 12 }}>
              Accès en lecture seule — vous ne pouvez pas ajouter de note.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
